/**
 * The shadcn/ui primitives this product actually uses, kept in one file.
 *
 * Same Radix + CVA + `cn` construction shadcn generates, consolidated because six
 * of these are under fifteen lines and a file each buys nothing. The Radix-backed
 * ones (checkbox, radio, select, dialog, table) stay in their own files.
 */
import {
  forwardRef,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react'
import { Slot } from '@radix-ui/react-slot'
import * as LabelPrimitive from '@radix-ui/react-label'
import * as SeparatorPrimitive from '@radix-ui/react-separator'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-colors disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-brand text-brand-fg hover:brightness-95 active:brightness-90',
        outline: 'border border-stone-300 bg-white text-stone-800 hover:bg-stone-100',
        ghost: 'text-stone-700 hover:bg-stone-100',
        danger: 'border border-red-200 bg-white text-red-700 hover:bg-red-50',
      },
      size: {
        // SPEC: nothing tappable is under 44px.
        default: 'min-h-tap px-4 text-[15px]',
        sm: 'min-h-[36px] px-3 text-sm',
        icon: 'min-h-tap w-11',
      },
    },
    defaultVariants: { variant: 'primary', size: 'default' },
  },
)

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & { asChild?: boolean; loading?: boolean }

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild, loading, children, disabled, ...props }, ref) => {
    const classes = cn(buttonVariants({ variant, size }), className)

    /*
     * `asChild` hands the styling to a single child element (a router <Link>, an
     * <a>), so it must pass exactly one child through — adding a spinner beside it
     * makes Radix's Slot throw. A link has nothing to be pending about anyway.
     */
    if (asChild) {
      return (
        <Slot ref={ref} className={classes} {...props}>
          {children}
        </Slot>
      )
    }

    return (
      <button ref={ref} className={classes} disabled={disabled || loading} {...props}>
        {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
        {children}
      </button>
    )
  },
)
Button.displayName = 'Button'

export const Card = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('rounded-lg border border-stone-200 bg-white', className)} {...props} />
)

export const CardHeader = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col gap-1 p-4 pb-2', className)} {...props} />
)

export const CardTitle = ({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) => (
  <h2 className={cn('text-base font-semibold', className)} {...props} />
)

export const CardDescription = ({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) => (
  <p className={cn('text-sm text-stone-500', className)} {...props} />
)

export const CardContent = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('p-4 pt-2', className)} {...props} />
)

export const CardFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex items-center gap-2 p-4 pt-0', className)} {...props} />
)

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => (
    <input
      ref={ref}
      className={cn(
        'min-h-tap w-full rounded-lg border border-stone-300 bg-white px-3 text-stone-900 placeholder:text-stone-400',
        'focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30',
        'aria-[invalid=true]:border-red-400 aria-[invalid=true]:ring-red-200',
        className,
      )}
      {...props}
    />
  ),
)
Input.displayName = 'Input'

/*
 * The same control as `Input`, taller. Sharing the class list rather than copying it
 * keeps the focus ring and the invalid state from drifting apart the first time one
 * of them is adjusted.
 */
const FIELD_CLASS =
  'w-full rounded-lg border border-stone-300 bg-white px-3 text-stone-900 placeholder:text-stone-400 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30 aria-[invalid=true]:border-red-400 aria-[invalid=true]:ring-red-200'

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  ({ className, ...props }, ref) => (
    <textarea ref={ref} className={cn(FIELD_CLASS, 'min-h-24 resize-y py-2.5', className)} {...props} />
  ),
)
Textarea.displayName = 'Textarea'

export const Label = forwardRef<
  HTMLLabelElement,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => (
  <LabelPrimitive.Root
    ref={ref}
    className={cn('text-sm font-medium text-stone-700', className)}
    {...props}
  />
))
Label.displayName = 'Label'

/** The one place a validation message is rendered, so they all look the same. */
export const FieldError = ({ children }: { children?: string }) =>
  children ? (
    <p className="text-sm text-red-600" role="alert">
      {children}
    </p>
  ) : null

export const Separator = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof SeparatorPrimitive.Root>) => (
  <SeparatorPrimitive.Root className={cn('h-px w-full bg-stone-200', className)} {...props} />
)

const badgeVariants = cva('inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium', {
  variants: {
    tone: {
      neutral: 'bg-stone-100 text-stone-700',
      brand: 'bg-brand/10 text-brand',
      success: 'bg-emerald-100 text-emerald-800',
      warning: 'bg-amber-100 text-amber-800',
      danger: 'bg-red-100 text-red-700',
      info: 'bg-sky-100 text-sky-800',
    },
  },
  defaultVariants: { tone: 'neutral' },
})

export type BadgeProps = HTMLAttributes<HTMLSpanElement> & VariantProps<typeof badgeVariants>

export const Badge = ({ className, tone, ...props }: BadgeProps) => (
  <span className={cn(badgeVariants({ tone }), className)} {...props} />
)

export const Skeleton = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('animate-pulse rounded-md bg-stone-200', className)} {...props} />
)

export const Spinner = ({ className }: { className?: string }) => (
  <Loader2 className={cn('size-5 animate-spin text-stone-400', className)} aria-hidden />
)

/**
 * Loading has to SAY it is loading.
 *
 * A lone `Skeleton` block pulses, but a grey rectangle with no words is read as a
 * screen that failed to load — which is exactly how it was reported. A spinner is
 * motion that looks like work and the label removes the doubt; together they are the
 * difference between "still coming" and "broken".
 *
 * `role="status"` carries the same news to a screen reader. `Skeleton` never did:
 * it is decorative markup, so a non-sighted admin got silence either way.
 */
export const LoadingState = ({ label = 'กำลังโหลด…', className }: { label?: string; className?: string }) => (
  <div
    role="status"
    aria-live="polite"
    className={cn('flex items-center justify-center gap-2.5 p-8 text-sm text-stone-500', className)}
  >
    <Spinner />
    <span>{label}</span>
  </div>
)
